// src/components/table/Sidebar.jsx
import { BarChart2, ChevronDown, EllipsisVertical } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { useState } from "react";
import AddEditTable from "../backoffice/table-floor/AddEditTable";
import { toast } from "react-hot-toast";
import { updateTableSize } from "@/api/table-floor/tableFloor";
import API from "@/api/lib/api";
import { DeleteConfirmDialog, DeMergeConfirmDialog } from "../dialog/common-dialog";
import { fetchAllTables, removeTableFromList } from "@/redux/slice/table-slice/table-slice";
import MergeTableModal from "./MergeTableModal";
import AddNewBookingFormSidebar from "./bookTable/AddNewBookingFormSidebar";
import hasPermission from "@/hooks/hasPermission";

export default function Sidebar({
  selectedTableSizeUpdate,
  isItemEdit,
  setIsItemEdit,
  activeTab
}) {
  const { selectedFloor, selectedRoom, tables, table_merges } = useSelector((state) => state.table.table);
  // console.log(table_merges);

  const dispatch = useDispatch();
  const [viewBy, setViewBy] = useState("");
  const [openView, setOpenView] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deMergeDialog, setDeMergeDialog] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [isMiniModalOpen, setIsMiniModalOpen] = useState(false);
  const [isDeMergeMiniModalOpen, setIsDeMergeMiniModalOpen] = useState(false);
  const [tempSelectedIdForCanvasEdit, setTempSelectedIdForCanvasEdit] = useState(null);
  // console.log(tempSelectedIdForCanvasEdit);

  const handleMenuOpen = (e, index) => {
    const rect = e.currentTarget.getBoundingClientRect();

    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.left - 100,
    });
    setIsMiniModalOpen(true);
    setTempSelectedIdForCanvasEdit(index);
  };
  const handleDeMergeMenuOpen = (e, index) => {
    const rect = e.currentTarget.getBoundingClientRect();

    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.left - 100,
    });
    setIsDeMergeMiniModalOpen(true);
    setTempSelectedIdForCanvasEdit(index);
  };

  const handleClose = () => {
    setIsMiniModalOpen(!isMiniModalOpen);
  };
  const handleDeMergeClose = () => {
    setIsDeMergeMiniModalOpen(!isDeMergeMiniModalOpen);
  };
  const [formType, setFormType] = useState("create");
  const [mergeFormType, setMergeFormType] = useState("create");
  const [isSingleVoucherModalOpen, setIsSingleVoucherModalOpen] = useState(false);
  const closeSingleVoucher = () => {
    setIsSingleVoucherModalOpen(false);
  };
  const [isAddNewBookingFormSidebarModalOpen, setIsAddNewBookingFormSidebarModalOpen] = useState(false);
  const closeAddNewBookingFormSidebar = () => {
    setIsAddNewBookingFormSidebarModalOpen(false);
  };

  const updateTable = async () => {
    try {
      let res;

      res = await updateTableSize(selectedTableSizeUpdate.id, { table_size: selectedTableSizeUpdate.table_size });

      if (res.status === "success") {
        setIsItemEdit(false)
        toast.success("Table updated successfully!");
        // refresh the tables for current room from server (preferred)
        if (selectedRoom?.id) dispatch(fetchAllTables(selectedRoom.id));
        // fallback: if server doesn't return updated list, we could also dispatch updateTableInList
      } else {
        toast.error(res.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to process Floor Card");
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialog(false);
  };
  const closeDeMergeDialog = () => {
    setDeMergeDialog(false);
  };

  const deMergeHandler = async () => {
    try {
      const table_ids = {
        table_ids: tempSelectedIdForCanvasEdit.tables.map(t => t.id),
      };

      let response = await API.put(
        `restaurant/table-merges/${tempSelectedIdForCanvasEdit.id}/unmerge`,
        table_ids
      );

      console.log(response)

      if (response.status === 'failure') {
        toast.error(response.message);
        return;
      }

      toast.success("Demerged successfully");

      // refresh tables of current room
      if (selectedRoom?.id) {
        dispatch(fetchAllTables(selectedRoom.id));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || "Something went wrong!.");
    } finally {
      closeDeMergeDialog();
    }
  };


  const deleteHandler = async () => {
    try {
      await API.delete(`restaurant/tables/${tempSelectedIdForCanvasEdit.id}`);
      toast.success("Deleted successfully");
      // if server deletion succeeded, refresh tables of current room
      if (selectedRoom?.id) {
        dispatch(fetchAllTables(selectedRoom.id));
      } else {
        // fallback: remove from local store
        dispatch(removeTableFromList(tempSelectedIdForCanvasEdit.id || tempSelectedIdForCanvasEdit.table_size?.id));
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong!.");
    } finally {
      closeDeleteDialog();
    }
  };

  const [isMergeTableModalOpen, setIsMergeTableModalOpen] = useState(false);
  const closeMergeTableModal = () => {
    setIsMergeTableModalOpen(false);
  };

  const filteredTables = tables.filter((table) => {
    if (table.is_visible === false) return false;

    if (viewBy === "booked") {
      return table.bookings && table.bookings.length > 0;
    }

    if (viewBy === "free") {
      return !table.bookings || table.bookings.length === 0;
    }

    return true; // All
  });

  return (
    <div className=" w-[300px] tab-max:w-96 h-[calc(100vh-120px)] pt-5 bg-[#1b1a1a] border rounded shadow border-gray-700 flex flex-col mr-2.5 relative">
      <div className="pl-4 pr-2 flex justify-between items-center">
        <p className="text-xl font-medium">Table</p>
        {activeTab === "book" ?
          <div className="flex space-x-2 text-sm font-normal">
            {
              hasPermission("table.book_table.create_booking") && <button className="bg-[#ffbb00] text-black px-2.5 h-10 rounded" onClick={() => { setIsAddNewBookingFormSidebarModalOpen(true) }}>Book Table</button>
            }
            {/* VIEW BY */}
            <div className="relative">
              <button
                onClick={() => setOpenView(!openView)}
                className="h-full flex items-center gap-2 bg-[#262626] px-3 py-1.5 rounded-md text-sm"
              >
                <BarChart2 size={14} />
                <span className="text-gray-300">{viewBy ? viewBy : 'View by'}</span>
                <ChevronDown size={14} />
              </button>

              {openView && (
                <div className="absolute right-0 mt-2 w-44 bg-[#262626] rounded-md overflow-hidden shadow-lg z-10">
                  {[{ label: "All", value: "" },
                  { label: "Free ", value: "free" },
                  { label: "Booked", value: "booked" }
                  ].map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => {
                        setViewBy(item.value);
                        setOpenView(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-white/10 border-gray-700 border-b ${viewBy === item.value ? "bg-white/10" : ""
                        }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div> :
          <div className="flex space-x-2 text-sm font-normal">
            {hasPermission("table.add_edit_table.merge_table") && (
              <button className="bg-[#ffbb00] text-black px-2.5 h-10 rounded" onClick={() => { setIsMergeTableModalOpen(true); setMergeFormType('create'); setIsItemEdit(false) }}>Merge Table</button>
            )}
            {hasPermission("table.add_edit_table.add_table") && (
              <button className="bg-[#ffbb00] text-black px-2.5 h-10 rounded" onClick={() => { setIsSingleVoucherModalOpen(true); setFormType(('create')); setIsItemEdit(false) }}>
                Add Table
              </button>
            )}
          </div>
        }
      </div>

      <div className="flex-1 overflow-auto ml-4 mr-2 mt-4 border rounded bg-[#2d2d2d]">
        <div className="flex justify-between mx-3 pt-2.5 pb-3 text-base font-medium">
          <p>Floor: {selectedFloor?.name}</p>
          <p>Room: {selectedRoom?.name}</p>
        </div>

        {filteredTables.map((t, i) => (
          <div
            key={i}
            className="flex justify-between items-center px-3 py-3 border-b border-[#393C49] hover:bg-gray-800"
          >
            <div className="flex items-center space-x-3">
              <div className="w-7 h-7 bg-[#bababa] text-black flex items-center justify-center rounded-full text-xs font-bold">
                {t.name}
              </div>
              <div className="font-normal text-sm">{t.name}</div>
            </div>

            <div className="flex items-center gap-1">
              <p className="opacity-60 font-normal text-sm">x{t.seat}</p>

              {/* ICON CLICK */}
              <div
                className="cursor-pointer"
                onClick={(e) => handleMenuOpen(e, t)}
              >
                <EllipsisVertical />
              </div>
            </div>
          </div>
        ))}
        {table_merges.map((t) => {
          const mergedTableName = t.tables.map(tb => tb.name).join(" + ");
          // const totalSeat = t.tables.length * 6;

          return (
            <div
              key={t.id}
              className="flex justify-between items-center px-3 py-3 border-b border-[#393C49] hover:bg-gray-800"
            >
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1">
                  {t.tables.map((tb, index) => (
                    <div key={tb.id} className="flex items-center space-x-1">
                      <div className="w-7 h-7 bg-[#bababa] text-black flex items-center justify-center rounded-full text-xs font-bold">
                        {tb.name}
                      </div>

                      {index < t.tables.length - 1 && (
                        <span className="text-white text-sm font-medium">+</span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="font-normal text-sm">
                  {mergedTableName}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <p className="opacity-60 font-normal text-sm">
                  {/* x{totalSeat} */}
                  x12
                </p>

                <div
                  className="cursor-pointer"
                  onClick={(e) => handleDeMergeMenuOpen(e, t)}
                >
                  <EllipsisVertical />
                </div>
              </div>
            </div>
          );
        })}


      </div>
      {isItemEdit && (
        <div className="text-end m-2">
          <button onClick={() => setIsItemEdit(false)} className="text-xs font-normal px-4 py-1 text-white bg-red-600 m-3 mr-0 rounded-sm">
            Cancel
          </button>
          <button className="text-xs font-normal px-4 py-1 text-white bg-[#34C759] m-3 rounded-sm" onClick={updateTable}>
            Save
          </button>
        </div>
      )}

      {/* OVERLAY (for outside click) */}
      {(isMiniModalOpen || isDeMergeMiniModalOpen) && <div className="fixed inset-0 z-[999]" onClick={() => { setIsMiniModalOpen(false); setIsDeMergeMiniModalOpen(false) }}></div>}

      {/* MINI MODAL */}
      {isMiniModalOpen && (
        <div
          className="fixed z-[1000] bg-[#3a3a3a] border border-gray-600 rounded shadow-lg w-36"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
          }}
        >
          {/* <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { handleClose(); }}>
            Edit Table
          </button> */}
          {hasPermission("table.add_edit_table.edit_table") && (
            <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { handleClose(); setFormType("update"); setIsSingleVoucherModalOpen(true); }}>
              Edit
            </button>
          )}
          <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { handleClose(); setFormType("view"); setIsSingleVoucherModalOpen(true); }}>
            View Details
          </button>
          <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { handleClose(); setDeleteDialog(true); }}>
            Delete
          </button>
        </div>
      )}
      {isDeMergeMiniModalOpen && (
        <div
          className="fixed z-[1000] bg-[#3a3a3a] border border-gray-600 rounded shadow-lg w-36"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
          }}
        >
          {/* <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { handleClose(); }}>
            Edit Table
          </button> */}
          {hasPermission("table.add_edit_table.edit_table") && (
            <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { handleDeMergeClose(); setMergeFormType("update"); setIsMergeTableModalOpen(true); }}>
              Edit
            </button>
          )}
          <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { handleDeMergeClose(); setMergeFormType("view"); setIsMergeTableModalOpen(true); }}>
            View Details
          </button>
          <button className="w-full text-left px-4 py-2 hover:bg-gray-700" onClick={() => { handleDeMergeClose(); setDeMergeDialog(true); }}>
            Demerge
          </button>
        </div>
      )}

      <AddNewBookingFormSidebar
        isOpen={isAddNewBookingFormSidebarModalOpen}
        onClose={closeAddNewBookingFormSidebar}
        itemsDialog={tempSelectedIdForCanvasEdit}
      />
      <AddEditTable
        isOpen={isSingleVoucherModalOpen}
        onClose={closeSingleVoucher}
        formType={formType}
        setFormType={setFormType}
        itemsDialog={tempSelectedIdForCanvasEdit}
      />
      <DeleteConfirmDialog
        isOpen={deleteDialog}
        onClose={closeDeleteDialog}
        onConfirm={() => {
          deleteHandler();
        }}
      />
      <DeMergeConfirmDialog
        isOpen={deMergeDialog}
        onClose={closeDeMergeDialog}
        onConfirm={() => {
          deMergeHandler();
        }}
      />
      <MergeTableModal
        isOpen={isMergeTableModalOpen}
        onClose={closeMergeTableModal}
        itemsDialog={tempSelectedIdForCanvasEdit}
        formType={mergeFormType}
      />
    </div>
  );
}
